import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import Modal from '../common/Modal';

const CommercialValidationModal = ({ requestId, onClose, onValidated }) => {
  const [comment, setComment] = useState('');
  const [avoirFraisNumber, setAvoirFraisNumber] = useState('');
  const [avoirUHTNumber, setAvoirUHTNumber] = useState('');
  const [hasFrais, setHasFrais] = useState(false);
  const [hasUHT, setHasUHT] = useState(false);
  const [hasUndefined, setHasUndefined] = useState(false);
  // Lignes de la demande et prix appliqués (texte saisi), par id de produit.
  const [lines, setLines] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.requests.getProducts(requestId);
        const products = response.data.data;
        setHasFrais(products.some(p => p.productType === 'frais'));
        setHasUHT(products.some(p => p.productType === 'uht'));
        setHasUndefined(products.some(p => !p.productType));
        setLines(products);
      } catch (err) {
        setError('Impossible de charger les produits de la demande.');
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [requestId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prix non transmis : le serveur applique le tarif catalogue.
      const payload = { comment };
      if (hasFrais) payload.avoirFraisNumber = avoirFraisNumber;
      if (hasUHT) payload.avoirUHTNumber = avoirUHTNumber;

      const response = await api.commercial.validate(requestId, payload);
      toast.success('Demande validée avec succès. Note de remboursement générée.');
      if (onValidated) onValidated(response.data.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la validation';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const totalHT = lines.reduce((sum, line) => sum + (line.unitPrice || 0) * line.quantity, 0);
  const formatDA = (n) => `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA`;

  const isSubmitDisabled = loading || loadingProducts || hasUndefined
    || (hasFrais && !avoirFraisNumber.trim())
    || (hasUHT && !avoirUHTNumber.trim());

  return (
    <Modal
      open
      onClose={onClose}
      title="Validation Service Commercial"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="modal-btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="commercial-validation-form"
            disabled={isSubmitDisabled}
            className="modal-btn-success"
          >
            {loading ? 'Validation...' : 'Valider et générer la note de remboursement'}
          </button>
        </>
      }
    >
      <form id="commercial-validation-form" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-500 mb-4">
                      En validant cette demande, vous allez générer une note de remboursement qui servira de document officiel.
                    </p>

                    {error && (
                      <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
                        {error}
                      </div>
                    )}

                    {loadingProducts ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : hasUndefined ? (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-md text-sm">
                        <strong>Action requise :</strong> Certains produits de cette demande n'ont pas de type défini (Frais/UHT).
                        Veuillez contacter l'administrateur avant de valider.
                      </div>
                    ) : (
                      <>
                        {hasFrais && (
                          <div className="mb-4">
                            <label htmlFor="avoirFraisNumber" className="block text-sm font-medium text-gray-700">
                              N° avoir Frais <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="avoirFraisNumber"
                              value={avoirFraisNumber}
                              onChange={e => setAvoirFraisNumber(e.target.value)}
                              required
                              disabled={loading}
                              placeholder="Ex: AV-2026-001"
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                        )}

                        {hasUHT && (
                          <div className="mb-4">
                            <label htmlFor="avoirUHTNumber" className="block text-sm font-medium text-gray-700">
                              N° avoir UHT <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="avoirUHTNumber"
                              value={avoirUHTNumber}
                              onChange={e => setAvoirUHTNumber(e.target.value)}
                              required
                              disabled={loading}
                              placeholder="Ex: AV-2026-002"
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                        )}

                        {/* Prix appliqués = tarif catalogue, non modifiables. */}
                        <div className="mb-4">
                          <p className="block text-sm font-medium text-gray-700">Prix unitaires appliqués</p>
                          <p className="text-xs text-gray-500 mb-2">
                            Les prix sont ceux du catalogue et seront figés sur la demande.
                          </p>
                          <div className="overflow-x-auto border border-gray-200 rounded-md">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                <tr>
                                  <th className="px-3 py-2 text-left">Produit</th>
                                  <th className="px-3 py-2 text-right">Qté</th>
                                  <th className="px-3 py-2 text-right">Prix unitaire HT</th>
                                  <th className="px-3 py-2 text-right">Total HT</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {lines.map(line => (
                                  <tr key={line.productId}>
                                    <td className="px-3 py-2 text-gray-900">{line.name}</td>
                                    <td className="px-3 py-2 text-right text-gray-700">{line.quantity}</td>
                                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">{formatDA(line.unitPrice || 0)}</td>
                                    <td className="px-3 py-2 text-right text-gray-900 whitespace-nowrap">
                                      {formatDA((line.unitPrice || 0) * line.quantity)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50 font-medium">
                                <tr>
                                  <td className="px-3 py-2 text-right" colSpan={3}>Total HT</td>
                                  <td className="px-3 py-2 text-right whitespace-nowrap">{formatDA(totalHT)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
                            Commentaire (optionnel)
                          </label>
                          <textarea
                            id="comment"
                            rows="3"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            disabled={loading}
                            placeholder="Ajouter un commentaire..."
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md"
                          />
                        </div>

                        <div className="mt-4 text-sm text-gray-500">
                          <p>
                            <strong>Note :</strong> Après validation, la note d'avoir est générée automatiquement. Cette validation est la dernière étape du processus.
                          </p>
                        </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default CommercialValidationModal;
